import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Select, TextInput, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button as SharedButton } from '../../../../components/Button';
import { PrinterView, printersService } from '../../../../services/printers/printers.service';
import { extractServiceErrorMessage } from '../../../../services/shared/service-error.util';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { getPrinterDisplayName } from 'shared-models/browser';
import { noSelectionTextInputProps } from '../receipt/receipt-input.utils';
import classes from './shared/settings-shared.module.scss';

type PrinterRoutingSectionProps = {
  tenantId: string;
  employeeSession: string;
  printers: PrinterView[];
};

const CLEAR_ROUTE_VALUE = 'none';

type PendingRoute = { origin: PrinterView; targetId: number | null; targetName: string | null };

// Summary: Manager POS-side printer routing override — redirect printer X's traffic to Y.
// Selecting a target opens a confirm guard (Rule 39); the PATCH sends only routeToPrinterId and the
// printer.upserted SSE delta patches the row, so no manual refetch is needed.
export const PrinterRoutingSection: React.FC<PrinterRoutingSectionProps> = ({
  tenantId,
  employeeSession,
  printers,
}) => {
  const [pending, setPending] = useState<PendingRoute | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingDisplayNameId, setSavingDisplayNameId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const confirmInFlightRef = useRef(false);
  const displayNameInFlightIdsRef = useRef(new Set<number>());

  // Defensive: a broken upstream SSE merge may hand us undefined/holes; never crash.
  const activePrinters = useMemo(
    () => (Array.isArray(printers) ? printers.filter((printer) => printer && printer.active) : []),
    [printers],
  );

  const handleSelect = useCallback(
    (origin: PrinterView, value: string | null) => {
      const targetId = value === null || value === CLEAR_ROUTE_VALUE ? null : Number(value);
      if (targetId === (origin.routeToPrinterId ?? null)) {
        return;
      }
      const target = targetId === null ? null : activePrinters.find((printer) => printer.id === targetId);
      const targetName = target ? getPrinterDisplayName(target) : null;
      setError(null);
      setPending({ origin, targetId, targetName });
    },
    [activePrinters],
  );

  const handleConfirm = useCallback(async () => {
    if (!pending || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await printersService.updatePrinterRoute(tenantId, pending.origin.id, pending.targetId, employeeSession);
      setPending(null);
    } catch (err: unknown) {
      setError(extractServiceErrorMessage(err, 'עדכון הניתוב נכשל'));
    } finally {
      confirmInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [pending, tenantId, employeeSession]);

  const handleDisplayNameSubmit = useCallback(async (printer: PrinterView, event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (displayNameInFlightIdsRef.current.has(printer.id)) return;

    const rawName = new FormData(event.currentTarget).get('customName');
    const customName = typeof rawName === 'string' && rawName.trim() ? rawName.trim() : null;
    if (customName === (printer.customName ?? null)) return;

    displayNameInFlightIdsRef.current.add(printer.id);
    setSavingDisplayNameId(printer.id);
    setError(null);
    try {
      await printersService.updatePrinterDisplayName(tenantId, printer.id, customName, employeeSession);
    } catch (err: unknown) {
      setError(extractServiceErrorMessage(err, 'עדכון שם התצוגה נכשל'));
    } finally {
      displayNameInFlightIdsRef.current.delete(printer.id);
      setSavingDisplayNameId(null);
    }
  }, [tenantId, employeeSession]);

  return (
    <section data-testid="printer-routing-section" className={classes.section}>
      <h3 className={classes.sectionHeading}>ניתוב מדפסות</h3>
      <p className={classes.lead}>
        נתב מדפסת שהתקלקלה למדפסת אחרת — כל בון או קבלה שהיו מודפסים במדפסת המקור יודפסו ביעד עד לביטול הניתוב.
      </p>

      {error && <div className={classes.error}>{error}</div>}

      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>מדפסת</th>
              <th>נתב אל</th>
              <th>סטטוס</th>
              <th>שם תצוגה</th>
            </tr>
          </thead>
          <tbody>
            {activePrinters.map((printer) => {
              const targetOptions = activePrinters
                .filter((candidate) => candidate.id !== printer.id)
                .map((candidate) => ({ value: String(candidate.id), label: getPrinterDisplayName(candidate) }));
              const routedId = printer.routeToPrinterId ?? null;
              const routedActive = routedId != null && targetOptions.some((o) => o.value === String(routedId));
              // A disabled routed target is gone from options; keep it shown (disabled).
              const routedGoneOption =
                routedId != null && !routedActive
                  ? [{ value: String(routedId), label: `${printer.routeToPrinterName ?? 'מדפסת'} (מושבתת)`, disabled: true }]
                  : [];
              return (
                <tr key={printer.id} data-testid={`printer-routing-row-${printer.id}`}>
                  <td>{printer.name}</td>
                  <td className={classes.parentCell}>
                    <Select
                      data-testid={`printer-routing-select-${printer.id}`}
                      aria-label={`ניתוב ${getPrinterDisplayName(printer)}`}
                      data={[{ value: CLEAR_ROUTE_VALUE, label: 'ללא ניתוב' }, ...targetOptions, ...routedGoneOption]}
                      value={routedId != null ? String(routedId) : CLEAR_ROUTE_VALUE}
                      onChange={(value) => handleSelect(printer, value)}
                      disabled={isSaving || (targetOptions.length === 0 && routedId == null)}
                      radius="md"
                    />
                  </td>
                  <td>
                    {routedId != null ? (
                      <span className={classes.statusInactive} data-testid={`printer-routing-hint-${printer.id}`}>
                        מנותב אל: {printer.routeToPrinterName ?? '—'}{routedActive ? '' : ' (מושבתת)'}
                      </span>
                    ) : (
                      <span className={classes.statusActive}>ללא ניתוב</span>
                    )}
                  </td>
                  <td>
                    <form className={classes.displayNameForm} onSubmit={(event) => void handleDisplayNameSubmit(printer, event)}>
                      <TextInput
                        name="customName"
                        data-testid={`printer-custom-name-${printer.id}`}
                        aria-label={`שם תצוגה ${printer.name}`}
                        defaultValue={printer.customName ?? ''}
                        placeholder={printer.name}
                        maxLength={120}
                        disabled={savingDisplayNameId === printer.id}
                        radius="md"
                        {...noSelectionTextInputProps}
                      />
                      <Tooltip label="שמור שם תצוגה" withArrow>
                        <span>
                          <SharedButton
                            type="submit"
                            variant="icon"
                            className={classes.displayNameSave}
                            dataTestId={`printer-custom-name-save-${printer.id}`}
                            disabled={savingDisplayNameId === printer.id}
                            ariaLabel="שמור שם תצוגה"
                          >
                            <FontAwesomeIcon icon={faCheck as IconProp} aria-hidden="true" />
                          </SharedButton>
                        </span>
                      </Tooltip>
                    </form>
                  </td>
                </tr>
              );
            })}
            {activePrinters.length === 0 && (
              <tr>
                <td colSpan={4} className={classes.empty}>אין מדפסות פעילות לניתוב.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        opened={pending !== null}
        onClose={() => (isSaving ? undefined : setPending(null))}
        title="אישור ניתוב מדפסת"
        closeButtonProps={{ 'data-testid': 'printer-routing-confirm-close' } as Record<string, string>}
        centered
        radius="md"
        classNames={{ root: navyModal.root }}
      >
        {pending && (
          <div className={classes.section} data-testid="printer-routing-confirm">
            <p className={classes.lead}>
              {pending.targetId === null
                ? `לבטל את הניתוב של «${pending.origin.name}»?`
                : `לנתב את «${pending.origin.name}» אל «${pending.targetName ?? 'המדפסת שנבחרה'}»? כל התעבורה תעבור ליעד.`}
            </p>
            <div className={classes.formActions}>
              <SharedButton
                variant="cancel"
                size="sm"
                dataTestId="printer-routing-cancel"
                onClick={() => setPending(null)}
                disabled={isSaving}
              >
                ביטול
              </SharedButton>
              <SharedButton
                variant="save"
                size="sm"
                dataTestId="printer-routing-apply"
                onClick={() => void handleConfirm()}
                disabled={isSaving}
              >
                {isSaving ? 'שומר...' : 'אישור'}
              </SharedButton>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};
